import { useState, useEffect, useCallback } from 'react';
import type { User, NotificationSettings } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserNotificationSettings,
  updateUser,
  sendPasswordReset,
  setUserAdminStatus,
} from '../services/usersService';

interface UserModalProps {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}

export default function UserModal({ user, onClose, onSaved }: UserModalProps) {
  const { user: currentUser } = useAuth();
  const [name, setName] = useState(user.name);
  const [isAdmin, setIsAdmin] = useState(user.isAdmin ?? false);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const isSelf = currentUser?.uid === user.id;

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      setSettings(await getUserNotificationSettings(user.id));
    } catch {
      // non-fatal
    } finally {
      setLoadingSettings(false);
    }
  }, [user.id]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updates: Promise<void>[] = [updateUser(user.id, { name: name.trim() })];
      if (!isSelf && isAdmin !== (user.isAdmin ?? false)) {
        updates.push(setUserAdminStatus(user.id, isAdmin));
      }
      await Promise.all(updates);
      onSaved();
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordReset() {
    if (!window.confirm(`Send a password reset email to ${user.email}?`)) return;
    setSendingReset(true);
    setError(null);
    setResetSuccess(false);
    try {
      await sendPasswordReset(user.email);
      setResetSuccess(true);
    } catch {
      setError('Failed to send password reset email.');
    } finally {
      setSendingReset(false);
    }
  }

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">Edit User</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {resetSuccess && (
                <div className="alert alert-success" role="alert">
                  Password reset email sent to {user.email}.
                </div>
              )}

              {/* Profile */}
              <h6 className="mb-3">Profile</h6>
              <div className="row g-3 mb-4">
                <div className="col-12">
                  <label className="form-label">Email</label>
                  <input type="text" className="form-control" value={user.email} disabled />
                </div>
                <div className="col-12">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                </div>
                <div className="col-12">
                  <label className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isAdmin}
                      disabled={saving || isSelf}
                      onChange={e => setIsAdmin(e.target.checked)}
                    />
                    <span className="form-check-label">Admin</span>
                  </label>
                  {isSelf && (
                    <div className="form-text text-muted">
                      You cannot change your own admin status.
                    </div>
                  )}
                </div>
              </div>

              {/* Notification settings — read-only */}
              <h6 className="mb-3">Notification Settings</h6>
              {loadingSettings ? (
                <div className="text-muted">Loading settings…</div>
              ) : settings === null ? (
                <div className="text-muted">No notification settings found for this user.</div>
              ) : (
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label">Notifications</label>
                    <div>
                      {settings.enabled ? (
                        <span className="badge bg-success text-white">Enabled</span>
                      ) : (
                        <span className="badge bg-secondary text-white">Disabled</span>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">FCM Token</label>
                    <div>
                      {settings.fcmToken ? (
                        <span className="badge bg-success text-white">Present</span>
                      ) : (
                        <span className="badge bg-secondary text-white">Absent</span>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Notify today&apos;s forecast</label>
                    <div>{settings.notifyToday ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Notify tomorrow&apos;s forecast</label>
                    <div>{settings.notifyTomorrow ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Today notification time</label>
                    <div>{settings.notificationTimeToday}</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Tomorrow notification time</label>
                    <div>{settings.notificationTimeTomorrow}</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Timezone</label>
                    <div className="text-muted">{settings.timezone || '—'}</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Monitoring</label>
                    <div className="text-muted">
                      {settings.municipalityIds.length > 0
                        ? `${settings.municipalityIds.length} municipalit${settings.municipalityIds.length === 1 ? 'y' : 'ies'}`
                        : '—'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link link-secondary me-auto"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={sendingReset}
                onClick={handlePasswordReset}
              >
                {sendingReset ? 'Sending…' : 'Reset password'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
